import React, { useRef, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';

type PreviewRequest = {
  src: string;
  startTime: number;
  endTime: number | null;
  targetElement: HTMLElement;
};

export type ClipPreviewController = {
  show: (data: PreviewRequest) => void;
  hide: () => void;
};

const timestampCache = new Map<string, number>();

export const ClipPreview = React.forwardRef<ClipPreviewController>((_, ref) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [preview, setPreview] = useState<PreviewRequest | null>(null);

  const animationFrameRef = useRef<number | null>(null);

  const previewUniqueId = useMemo(() => {
    if (!preview) return '';
    return `${preview.src}@${preview.startTime}-${preview.endTime}`;
  }, [preview]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !preview) return;

    video.src = preview.src;

    const savedTime = timestampCache.get(previewUniqueId);
    video.currentTime = savedTime ?? preview.startTime;

    video.play().catch(() => {});

    if (preview.endTime == null) {
      video.loop = true;
      return;
    }

    video.loop = false;

    const watchTime = () => {
      if (!video) return;

      if (video.currentTime >= preview.endTime!)
        video.currentTime = preview.startTime;

      animationFrameRef.current = requestAnimationFrame(watchTime);
    };

    animationFrameRef.current = requestAnimationFrame(watchTime);

    return () => {
      video.pause();
      video.src = '';

      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };
  }, [preview, previewUniqueId]);

  React.useImperativeHandle(ref, () => ({
    show: (data) => setPreview(data),
    hide: () => {
      const video = videoRef.current;

      if (video && preview && video.currentTime > preview.startTime)
        timestampCache.set(previewUniqueId, video.currentTime);

      setPreview(null);
    },
  }));

  if (!preview) return null;

  const videoElement = (
    <video
      ref={videoRef}
      muted
      playsInline
      autoPlay
      className="absolute inset-0 z-1 pointer-events-none w-full h-full object-cover rounded-md"
    />
  );

  return createPortal(videoElement, preview.targetElement);
});
